import { useEffect, useState } from 'react';

import {
  deleteSupplementaryFields,
  getSupplementaryFields,
  patchFineConfigurations,
  updateSupplementaryFields
} from '../api';
import { SupplementaryField } from '../api/configurations/supplementary/types';
import { Error } from '../api/setup/getApiError';
import { defaultSelectableValues } from '../components/UserDatabase/SupplementaryInfoForm/utils';
import { FetchingState, RemindToFieldList } from '../contexts/constants';
import { SupplementaryData, SupplementaryFormValues, UseSupplementaryData } from './types';

export const useSupplementaryData = (projectId: string): UseSupplementaryData => {
  const [data, setData] = useState<SupplementaryData>({});
  const [fetchingState, setFetchingState] = useState<FetchingState>(FetchingState.Idle);
  const [getDataError, setGetDataError] = useState<Error | null>(null);

  const setPreparedData = (list: SupplementaryField[]) => {
    const fieldList = list.reduce((acc, item) => ({ ...acc, [item.name]: { ...item } }), {});
    setData(fieldList);
  };

  const getData = async () => {
    try {
      setFetchingState(FetchingState.Loading);
      const response = await getSupplementaryFields(projectId);
      setPreparedData(response);
      setFetchingState(FetchingState.Succeeded);
    } catch (err) {
      setGetDataError(err);
      setFetchingState(FetchingState.Failed);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const updateFields = async (values: SupplementaryFormValues) => {
    try {
      setFetchingState(FetchingState.Updating);
      const activeFields = Object.keys(data).reduce((acc, item) => {
        if ((values.fields as string[]).includes(item)) {
          return acc;
        }
        return [...acc, deleteSupplementaryFields(projectId, item)];
      }, []);
      await Promise.all(activeFields);

      const requestBody = (values.fields as string[]).reduce((acc, name) => {
        const confirmType = { confirmation_type: data[name]?.confirmation_type };
        return [
          ...acc,
          {
            ...values[name],
            remind_to_fill: RemindToFieldList.includes(name),
            selectable_values: defaultSelectableValues[name],
            ...confirmType
          }
        ];
      }, []);

      if (requestBody.length !== 0) {
        await updateSupplementaryFields(projectId, requestBody as SupplementaryField[]);
        if ((values.fields as string[]).includes('username')) {
          await patchFineConfigurations({ is_username_email: false }, projectId);
        }
      }
      setPreparedData(requestBody as SupplementaryField[]);
      setFetchingState(FetchingState.Succeeded);
    } catch (e) {
      setFetchingState(FetchingState.Failed);
    }
  };

  return {
    fieldOptions: data,
    getDataError,
    fetchingState,
    updateFields
  };
};
